import type { ReactNode, Ref } from "react";

import { FormFieldAssistiveText } from "#components/shared/FormFieldAssistiveText";
import { FormFieldHeader } from "#components/shared/FormFieldHeader";
import { cn, customTv } from "#utils";
import {
  Group as ReactAriaGroup,
  Input as ReactAriaInput,
  NumberField as ReactAriaNumberField,
  type NumberFieldProps as ReactAriaNumberFieldProps,
} from "react-aria-components";

const groupStyles = customTv({
  base: `
    flex items-center gap-x-sm bg-primary rounded-1 px-lg py-xs outline-none border border-outline-input-enabled
    hover:border-outline-input-hover hover:ring-1 hover:ring-outline-input-hover
    focus-within:!border-outline-input-focus focus-within:!ring-1 focus-within:!ring-outline-input-focus
  `,
  variants: {
    isDisabled: {
      true: "border-outline-input-disabled cursor-not-allowed bg-background-primary hover:ring-0 hover:border-outline-input-disabled",
    },
    isReadOnly: {
      true: "bg-background-secondary !border-transparent hover:!ring-0 focus-within:!ring-0 focus-within:!border-transparent",
    },
    isInvalid: {
      true: "border-outline-negative ring-1 ring-outline-negative",
    },
  },
});

const inputStyles = customTv({
  base: `
    flex-1 text-label-medium text-text-primary bg-transparent border-none outline-none leading-md-20 truncate
    placeholder:text-text-secondary disabled:placeholder:text-text-disabled
    disabled:cursor-not-allowed disabled:text-text-disabled
    [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none
  `,
  variants: {
    isReadOnly: {
      true: "!cursor-text !text-text-primary",
    },
  },
});

const unitStyles = customTv({
  base: "text-label-small text-text-secondary shrink-0",
  variants: {
    isDisabled: {
      true: "text-text-disabled",
    },
  },
});

/** NumberFieldProps interface for the NumberField component */
export interface NumberFieldProps extends Omit<
  ReactAriaNumberFieldProps,
  "className" | "children"
> {
  /** Label text displayed above the field */
  label?: string;
  /** Assistive text displayed below the field */
  assistiveText?: ReactNode;
  /** Tooltip text shown next to the label */
  tooltipText?: ReactNode;
  /** Additional CSS classes for the container */
  className?: string;
  /** Placeholder text for the input */
  placeholder?: string;
  /** Unit text to display after the input (e.g., "kg", "USD", "%") */
  unit?: string;
  /** Ref forwarded to the underlying <input> element */
  ref?: Ref<HTMLInputElement>;
}

/**
 * NumberField component - A numeric input field with optional unit display.
 */
export function NumberField(props: NumberFieldProps) {
  const {
    label,
    tooltipText,
    className,
    assistiveText,
    isDisabled,
    isReadOnly,
    isInvalid = false,
    placeholder = "0",
    unit,
    ref,
    "aria-label": ariaLabel,
    ...restProps
  } = props;

  return (
    <ReactAriaNumberField
      aria-label={ariaLabel ?? (label ? undefined : "number input")}
      className={cn("flex flex-col gap-[.15rem]", className)}
      isDisabled={isDisabled}
      isInvalid={isInvalid}
      isReadOnly={isReadOnly}
      {...restProps}
      slot={null}
    >
      <FormFieldHeader
        isDisabled={isDisabled}
        label={label}
        tooltipButtonProps={{ slot: null }}
        tooltipText={tooltipText}
      />
      <ReactAriaGroup
        className={groupStyles({
          isDisabled,
          isReadOnly,
          isInvalid,
        })}
      >
        <ReactAriaInput
          className={inputStyles({ isReadOnly })}
          placeholder={placeholder}
          ref={ref}
        />
        {unit ? <span className={unitStyles({ isDisabled })}>{unit}</span> : null}
      </ReactAriaGroup>
      <FormFieldAssistiveText isDisabled={isDisabled} isInvalid={isInvalid}>
        {assistiveText}
      </FormFieldAssistiveText>
    </ReactAriaNumberField>
  );
}
